import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type {
    PublishedColumnModel,
    PublishedCountryMapModel,
    PublishedFieldModel,
    PublishedRecordDetailModel,
    PublishedReviewListModel,
    PublishedRowModel,
    PublishedSnapshotModel,
} from "../types/publishedReview.types";
import type { ODataListResponse } from "../types/odata.types";
import type { ListQueryParams } from "./querying/listQueryParams";
import {
    MOCK_REVIEW_ID,
    mockAssociationsColumns,
    mockAssociationsRows,
    mockAssociationsTable,
    mockPublishedRecords,
    mockPublishedReview,
    mockPublishedSnapshot,
    mockRecordColumns,
} from "./mocks/climateAssociationsMock";
import { countryNamesByCode } from "./mocks/countryNames";
import { resolveCountryCodes } from "./mocks/countryResolver";

// Mock data — there are no backend endpoints for published reviews yet.
// Each query notes the endpoint it stands in for.

const MOCK_DELAY_MS = 300;

const mockDelay = () => new Promise(resolve => setTimeout(resolve, MOCK_DELAY_MS));

const mockPublishedReviews: PublishedReviewListModel[] = [mockPublishedReview];

// Server stand-in for the live join on ReviewRecord: `record.*` columns (e.g.
// the Source citation) aren't stored on published rows, so they're filled in
// from the record's current metadata before sorting and paging. A missing
// record leaves them null.
const recordsById = new Map(mockPublishedRecords.map(record => [record.id, record]));
const withRecordColumns = (row: PublishedRowModel): PublishedRowModel => {
    const record = recordsById.get(row.recordId);
    const recordValues = Object.fromEntries(mockRecordColumns.map(({ key, field }) => {
        const value = record?.[field] ?? null;
        return [key, Array.isArray(value) ? value.join(', ') : value];
    }));
    return { ...row, values: { ...recordValues, ...row.values } };
};

const isMockTable = (reviewId: string, tableId: string) =>
    reviewId === MOCK_REVIEW_ID && tableId === mockAssociationsTable.id;

const parseOrderBy = (orderBy?: string) => {
    if (!orderBy) return undefined;
    const [field, direction] = orderBy.split(' ');
    return { field, sign: direction === 'desc' ? -1 : 1 };
};

const sortReviews = (reviews: PublishedReviewListModel[], orderBy?: string) => {
    const sort = parseOrderBy(orderBy);
    if (!sort) return reviews;
    const field = sort.field as keyof PublishedReviewListModel;
    return [...reviews].sort((a, b) => {
        const left = a[field];
        const right = b[field];
        const result = typeof left === 'number' && typeof right === 'number'
            ? left - right
            : String(left).localeCompare(String(right));
        return result * sort.sign;
    });
};

// Server-side sort stand-in: nulls always last, numeric-aware string
// comparison, and the default (published) order as the tie-breaker. Unknown
// column keys are ignored, as the real endpoint would whitelist them.
const sortRows = (rows: PublishedRowModel[], columns: PublishedColumnModel[], orderBy?: string) => {
    const sort = parseOrderBy(orderBy);
    if (!sort || !columns.some(column => column.key === sort.field)) return rows;
    const ordinal = new Map(rows.map((row, index) => [row.id, index]));
    return [...rows].sort((a, b) => {
        const left = a.values[sort.field] ?? null;
        const right = b.values[sort.field] ?? null;
        if (left === null || right === null) {
            if (left !== right) return left === null ? 1 : -1;
        } else {
            const result = typeof left === 'number' && typeof right === 'number'
                ? left - right
                : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
            if (result !== 0) return result * sort.sign;
        }
        return ordinal.get(a.id)! - ordinal.get(b.id)!;
    });
};

const getNextPageParam = <T>(lastPage: ODataListResponse<T>, allPages: ODataListResponse<T>[]) => {
    const loaded = allPages.reduce((sum, page) => sum + page.value.length, 0);
    return loaded < lastPage['@odata.count'] ? loaded : undefined;
};

/** GET /published?$top&$skip&$orderby */
export const getPublishedReviewsInfiniteQuery = (params: Omit<ListQueryParams, 'skip'> = {}) => infiniteQueryOptions({
    queryKey: ['published-reviews', 'list', 'infinite', params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
        const sorted = sortReviews(mockPublishedReviews, params.orderBy);
        const top = params.top ?? sorted.length;
        await mockDelay();
        return {
            value: sorted.slice(pageParam, pageParam + top),
            '@odata.count': sorted.length,
        } satisfies ODataListResponse<PublishedReviewListModel>;
    },
    getNextPageParam,
    staleTime: 20 * 1000
});

/** GET /published/{reviewId} */
export const getPublishedReviewByIdQuery = (reviewId: string) => queryOptions({
    queryKey: ['published-reviews', 'single', reviewId],
    queryFn: async () => {
        await mockDelay();
        return mockPublishedReviews.find(review => review.id === reviewId) ?? null;
    },
    staleTime: 20 * 1000
});

/** GET /published/{reviewId}/snapshot — the current snapshot and its tables. */
export const getPublishedSnapshotQuery = (reviewId: string) => queryOptions({
    queryKey: ['published-reviews', 'snapshot', reviewId],
    queryFn: async (): Promise<PublishedSnapshotModel | null> => {
        await mockDelay();
        return reviewId === MOCK_REVIEW_ID ? mockPublishedSnapshot : null;
    },
    staleTime: 20 * 1000
});

/** GET /published/{reviewId}/tables/{tableId}/columns */
export const getPublishedTableColumnsQuery = (reviewId: string, tableId: string) => queryOptions({
    queryKey: ['published-reviews', 'tables', reviewId, tableId, 'columns'],
    queryFn: async (): Promise<PublishedColumnModel[]> => {
        await mockDelay();
        return isMockTable(reviewId, tableId) ? mockAssociationsColumns : [];
    },
    staleTime: 20 * 1000
});

/** GET /published/{reviewId}/tables/{tableId}/rows?$top&$skip&$orderby */
export const getPublishedTableRowsInfiniteQuery = (reviewId: string, tableId: string, params: Omit<ListQueryParams, 'skip'> = {}) => infiniteQueryOptions({
    queryKey: ['published-reviews', 'tables', reviewId, tableId, 'rows', 'infinite', params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
        const rows = isMockTable(reviewId, tableId) ? mockAssociationsRows.map(withRecordColumns) : [];
        const sorted = sortRows(rows, mockAssociationsColumns, params.orderBy);
        const top = params.top ?? sorted.length;
        await mockDelay();
        return {
            value: sorted.slice(pageParam, pageParam + top),
            '@odata.count': sorted.length,
        } satisfies ODataListResponse<PublishedRowModel>;
    },
    getNextPageParam,
    staleTime: 20 * 1000
});

// Server stand-in for the record endpoint: splits each table's columns by
// scope — record-level fields are shown once (taken from the record's first
// row, de-duplicated across tables), item-level columns stay with each row,
// and metadata columns are dropped since the metadata is returned separately.
const buildRecordDetail = (recordId: string): PublishedRecordDetailModel | null => {
    const record = recordsById.get(recordId);
    if (!record) return null;

    const tables = [{ table: mockAssociationsTable, columns: mockAssociationsColumns, rows: mockAssociationsRows }];

    const fields = new Map<string, PublishedFieldModel>();
    const recordTables = tables.map(({ table, columns, rows }) => {
        const recordRows = rows.filter(row => row.recordId === recordId);
        for (const column of columns.filter(c => c.scope === 'record')) {
            if (!fields.has(column.key)) {
                fields.set(column.key, { key: column.key, label: column.label, value: recordRows[0]?.values[column.key] ?? null });
            }
        }
        const itemColumns = columns.filter(c => c.scope === 'item');
        return {
            id: table.id,
            name: table.name,
            columns: itemColumns,
            rows: recordRows.map(row => ({
                ...row,
                values: Object.fromEntries(itemColumns.map(c => [c.key, row.values[c.key] ?? null])),
            })),
        };
    });

    return { record, fields: [...fields.values()], tables: recordTables };
};

/** GET /published/{reviewId}/records/{recordId} */
export const getPublishedRecordQuery = (reviewId: string, recordId: string) => queryOptions({
    queryKey: ['published-reviews', 'records', reviewId, recordId],
    queryFn: async (): Promise<PublishedRecordDetailModel | null> => {
        await mockDelay();
        return reviewId === MOCK_REVIEW_ID ? buildRecordDetail(recordId) : null;
    },
    staleTime: 20 * 1000
});

// Column holding each row's country. In the real system the projection would
// mark which column(s) carry location data.
const COUNTRY_COLUMN_KEY = 'country';

type Tally = { recordIds: Set<string>; associations: number };

const addTo = <K,>(tallies: Map<K, Tally>, key: K, recordId: string) => {
    const tally = tallies.get(key) ?? { recordIds: new Set<string>(), associations: 0 };
    tally.recordIds.add(recordId);
    tally.associations++;
    tallies.set(key, tally);
};

// Server stand-in: aggregates every row of the table (not just a loaded page)
// by country. A row naming several countries counts towards each of them.
const buildCountryMap = (rows: PublishedRowModel[]): PublishedCountryMapModel => {
    const byCountry = new Map<string, Tally>();
    const unplaced = new Map<string | null, Tally>();

    for (const row of rows) {
        const raw = row.values[COUNTRY_COLUMN_KEY];
        const value = typeof raw === 'string' && raw.trim() ? raw.trim() : null;
        const codes = resolveCountryCodes(value);
        if (codes.length === 0) addTo(unplaced, value, row.recordId);
        for (const code of codes) addTo(byCountry, code, row.recordId);
    }

    return {
        countries: [...byCountry]
            .map(([code, { recordIds, associations }]) => ({
                code,
                name: countryNamesByCode[code] ?? code,
                studies: recordIds.size,
                associations,
            }))
            .sort((a, b) => b.studies - a.studies || a.name.localeCompare(b.name)),
        unplaced: [...unplaced]
            .map(([value, { recordIds, associations }]) => ({ value, studies: recordIds.size, associations }))
            .sort((a, b) => b.associations - a.associations),
    };
};

/** GET /published/{reviewId}/tables/{tableId}/map/countries */
export const getPublishedTableCountriesQuery = (reviewId: string, tableId: string) => queryOptions({
    queryKey: ['published-reviews', 'tables', reviewId, tableId, 'map', 'countries'],
    queryFn: async (): Promise<PublishedCountryMapModel> => {
        await mockDelay();
        return buildCountryMap(isMockTable(reviewId, tableId) ? mockAssociationsRows : []);
    },
    staleTime: 20 * 1000
});
